// What a third-party reviewer's browser remembers between visits to a creative
// review link: one access token per package, and one device id.
//
// The access token comes back from `verify/complete` and lets this browser open
// the package again until it expires — at most 72 hours, never past the link's
// own expiry — without another email. It travels only in the JSON body of the
// public POSTs (`access`), never a header, a path, a query key or a log. The
// device id names this browser to the server, which decides from it whether
// this browser may record a decision (only the one that asked for the email and
// the one that first opened it may).
//
// Storage can be missing or throw (a private window, blocked site data, a full
// quota), so every access is wrapped like `cache/persister.ts`, and what could
// not be written is held in memory for the life of the tab. The server decides
// access either way; nothing here is a security boundary. The reviewer's email
// is never stored: `/package` returns it.

const ACCESS_PREFIX = 'shubert.review-access.v1:';
const DEVICE_KEY = 'shubert.review-device.v1';

/**
 * At most this many packages are remembered. A reviewer rarely holds more than
 * a handful of live links; past the cap, the entry that expires soonest goes.
 */
export const MAX_STORED_ACCESS = 50;

export interface ReviewerAccess {
  token: string;
  /** RFC 3339, as `verify/complete` returned it. */
  expires_at: string;
  /**
   * `shareKey(<share token>)` for the link this access was confirmed through,
   * so a reload of `#p=<token>` finds it without knowing the package id. Null
   * for a confirmation emailed before links carried the share token.
   */
  share_key: string | null;
}

export interface StoredAccess extends ReviewerAccess {
  packageId: string;
}

/** Writes the browser refused, kept for this tab only. */
const memory = new Map<string, string>();
let memoryDevice: string | null = null;
const listeners = new Set<() => void>();

function getItem(key: string): string | null {
  // What this tab holds comes first: it is only ever there because a newer
  // write was refused, so the stored value under the same key is older. A
  // browser that still reads but no longer writes (a full quota) would
  // otherwise answer with the previous reviewer's access.
  const held = memory.get(key);
  if (held !== undefined) return held;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function setItem(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
    memory.delete(key);
  } catch {
    memory.set(key, value);
  }
}

function removeItem(key: string): void {
  memory.delete(key);
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing stored there to remove.
  }
}

function accessKeys(): string[] {
  const keys = new Set<string>();
  try {
    const storage = window.localStorage;
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(ACCESS_PREFIX)) keys.add(key);
    }
  } catch {
    // Only the in-memory entries are known.
  }
  for (const key of memory.keys()) if (key.startsWith(ACCESS_PREFIX)) keys.add(key);
  return [...keys];
}

function notify(): void {
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // One listener's failure must not stop the others.
    }
  }
}

/**
 * FNV-1a (32-bit) of the share token, as 8 hex characters. Synchronous on
 * purpose: the LAN mobile preview is served over plain HTTP, where
 * `crypto.subtle` is undefined. It only finds this browser's own entry; the
 * server checks the token itself.
 */
export function shareKey(shareToken: string): string {
  let hash = 0x811c9dc5;
  const text = shareToken.toLowerCase();
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

function expired(access: ReviewerAccess, now: number): boolean {
  const at = Date.parse(access.expires_at);
  return !Number.isFinite(at) || at <= now;
}

function parse(raw: string | null): ReviewerAccess | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<ReviewerAccess> | null;
    if (
      !value
      || typeof value.token !== 'string'
      || !value.token
      || typeof value.expires_at !== 'string'
      || (value.share_key !== null && typeof value.share_key !== 'string')
    ) {
      return null;
    }
    return { token: value.token, expires_at: value.expires_at, share_key: value.share_key ?? null };
  } catch {
    return null;
  }
}

/**
 * Every live entry. Expired and unreadable ones are deleted on the way, so
 * nothing past its expiry is ever sent.
 */
export function listAccess(now = Date.now()): StoredAccess[] {
  const live: StoredAccess[] = [];
  for (const key of accessKeys()) {
    const access = parse(getItem(key));
    if (!access || expired(access, now)) {
      removeItem(key);
      continue;
    }
    live.push({ ...access, packageId: key.slice(ACCESS_PREFIX.length) });
  }
  return live;
}

/** Delete every expired or unreadable entry. */
export function pruneExpiredAccess(now = Date.now()): void {
  listAccess(now);
}

/** The live access for a package, if this browser holds one. */
export function readAccess(packageId: string, now = Date.now()): ReviewerAccess | null {
  const key = ACCESS_PREFIX + packageId;
  const access = parse(getItem(key));
  if (!access) {
    removeItem(key);
    return null;
  }
  if (expired(access, now)) {
    removeItem(key);
    return null;
  }
  return access;
}

/** The live access confirmed through this share link, if any. */
export function findAccessByShareToken(shareToken: string, now = Date.now()): StoredAccess | null {
  const wanted = shareKey(shareToken);
  const matches = listAccess(now).filter((entry) => entry.share_key === wanted);
  // Two packages cannot share a token; a collision of the 32-bit key can.
  // Prefer the entry that lasts longest; the server refuses a wrong one anyway.
  matches.sort((a, b) => Date.parse(b.expires_at) - Date.parse(a.expires_at));
  return matches[0] ?? null;
}

export function saveAccess(
  packageId: string,
  access: { token: string; expires_at: string; shareToken: string | null },
  now = Date.now(),
): void {
  const entry: ReviewerAccess = {
    token: access.token,
    expires_at: access.expires_at,
    share_key: access.shareToken ? shareKey(access.shareToken) : null,
  };
  if (expired(entry, now)) return;
  setItem(ACCESS_PREFIX + packageId, JSON.stringify(entry));
  const live = listAccess(now);
  if (live.length > MAX_STORED_ACCESS) {
    live
      .filter((stored) => stored.packageId !== packageId)
      .sort((a, b) => Date.parse(a.expires_at) - Date.parse(b.expires_at))
      .slice(0, live.length - MAX_STORED_ACCESS)
      .forEach((stored) => removeItem(ACCESS_PREFIX + stored.packageId));
  }
  notify();
}

export function clearAccess(packageId: string): void {
  removeItem(ACCESS_PREFIX + packageId);
  notify();
}

/**
 * Called whenever this tab, or another tab of the same browser, saves or
 * clears an access entry. Returns the unsubscribe.
 */
export function onAccessChange(listener: () => void): () => void {
  listeners.add(listener);
  const fromOtherTab = (event: StorageEvent) => {
    if (event.key === null || event.key.startsWith(ACCESS_PREFIX)) listener();
  };
  window.addEventListener('storage', fromOtherTab);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', fromOtherTab);
  };
}

const DEVICE_RE = /^[A-Za-z0-9_-]{43}$/;

function mintDevice(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * This browser's device id: 32 random bytes, base64url without padding, the
 * shape the server accepts (anything else it treats as no device). Minted
 * once and kept; if storage refuses it, the tab keeps one in memory, so this
 * browser stays the same device until it closes.
 */
export function reviewerDevice(): string {
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(DEVICE_KEY);
  } catch {
    // Fall through to the in-memory id.
  }
  if (stored && DEVICE_RE.test(stored)) return stored;
  if (memoryDevice) return memoryDevice;
  const device = mintDevice();
  try {
    window.localStorage.setItem(DEVICE_KEY, device);
  } catch {
    memoryDevice = device;
  }
  return device;
}

/** Test seam: forget what this module holds in memory. */
export function resetReviewerMemoryForTests(): void {
  memory.clear();
  memoryDevice = null;
  listeners.clear();
}
